"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { jwtDecode } from 'jwt-decode';
import { setOnAuthExpired } from '@/lib/apiClient';

interface User {
  id: string;
  email: string;
  name?: string;
}

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: (token: string) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const initializeAuth = () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          const decoded: { sub: string; email?: string; name?: string } = jwtDecode(token);
          setUser({ id: decoded.sub, email: decoded.email || 'N/A', name: decoded.name });
        }
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        localStorage.removeItem('accessToken');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    initializeAuth();
  }, []);

  useEffect(() => {
    setOnAuthExpired(() => {
      setUser(null);
      router.push('/login');
    });
    return () => setOnAuthExpired(null);
  }, [router]);

  const login = (token: string) => {
    localStorage.setItem('accessToken', token);
    const decoded: { sub: string; email?: string; name?: string } = jwtDecode(token);
    setUser({ id: decoded.sub, email: decoded.email || 'N/A', name: decoded.name });
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
  };

  // Render a loading screen while checking for the token
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-900">
        <p className="text-white">驗證身份中...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
